import React, { useEffect, useRef, useState } from 'react';
import { Code, Brain, Palette, Server, Zap, Star, TrendingUp, Award } from 'lucide-react';

const Skills = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [activeSkill, setActiveSkill] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const skillCategories = [
    {
      icon: Code,
      title: 'Programming',
      color: '#00D9FF',
      bgGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      skills: [
        { name: 'Python', level: 93, projects: 12 },
        { name: 'C++', level: 85, projects: 5 },
        { name: 'C', level: 80, projects: 3 },
        { name: 'SQL', level: 84, projects: 6 }
      ]
    },
    {
      icon: Brain,
      title: 'AI & ML',
      color: '#FF00FF',
      bgGradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      skills: [
        { name: 'Generative AI', level: 95, projects: 12 },
        { name: 'RAG', level: 92, projects: 7 },
        { name: 'CAMEL-AI', level: 90, projects: 5 },
        { name: 'LangChain', level: 88, projects: 6 },
        { name: 'CrewAI', level: 86, projects: 4 },
        { name: 'LLM Pipelines', level: 87, projects: 5 },
        { name: 'Multi-Agent Systems', level: 90, projects: 8 },
        { name: 'Machine Learning', level: 88, projects: 7 }
      ]
    },
    {
      icon: Palette,
      title: 'Frontend & Tools',
      color: '#00FF88',
      bgGradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      skills: [
        { name: 'React.js', level: 85, projects: 6 },
        { name: 'HTML', level: 90, projects: 10 },
        { name: 'CSS', level: 88, projects: 10 },
        { name: 'Streamlit', level: 82, projects: 5 },
        { name: 'Jupyter Notebook', level: 89, projects: 12 },
        { name: 'Git', level: 85, projects: 10 },
        { name: 'GitHub', level: 88, projects: 10 }
      ]
    },
    {
      icon: Server,
      title: 'Backend & Deployment',
      color: '#FFD700',
      bgGradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      skills: [
        { name: 'FastAPI', level: 88, projects: 8 },
        { name: 'API Development', level: 88, projects: 8 },
        { name: 'MongoDB', level: 80, projects: 4 },
        { name: 'SQLite', level: 84, projects: 7 },
        { name: 'FAISS / ChromaDB', level: 82, projects: 5 },
        { name: 'Automation', level: 90, projects: 6 }
      ]
    }
  ];

  const featuredSkills = [
    { icon: Zap, label: 'Hands-on Projects', value: '20+' },
    { icon: Star, label: 'DSA Solved', value: '200+' },
    { icon: TrendingUp, label: 'Core Technologies', value: '15+' },
    { icon: Award, label: 'Hackathon Win', value: '2025' }
  ];

  return (
    <section
      id="skills"
      ref={sectionRef}
      // Reduced py-24 to py-16
      className="relative py-16 px-6 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, var(--bg-primary) 0%, rgba(0, 217, 255, 0.02) 50%, var(--bg-primary) 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {/* Animated Background Patterns */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating Orbs - Scaled Down */}
        <div
          className="absolute top-10 right-1/4 w-48 h-48 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(0, 217, 255, 0.1) 0%, transparent 70%)',
            filter: 'blur(40px)',
            animation: 'float 10s ease-in-out infinite'
          }}
        />
        <div
          className="absolute bottom-10 left-1/4 w-60 h-60 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255, 0, 255, 0.1) 0%, transparent 70%)',
            filter: 'blur(50px)',
            animation: 'float 12s ease-in-out infinite reverse'
          }}
        />
        
        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(var(--brand-primary) 1px, transparent 1px),
              linear-gradient(90deg, var(--brand-primary) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      <div className="max-w-6xl mx-auto w-full relative z-10">
        {/* Section Title - Compact */}
        <div className={`text-center mb-12 ${isVisible ? 'animate-fade-down' : 'opacity-0'}`}>
          <div className="inline-block mb-2">
            <span
              className="text-xs font-bold tracking-[0.3em] uppercase"
              style={{
                color: 'var(--brand-primary)',
                letterSpacing: '0.3em'
              }}
            >
              Technical Proficiency
            </span>
          </div>
          {/* Smaller Heading */}
          <h2
            className="text-4xl md:text-6xl font-bold mb-4"
            style={{
              background: 'linear-gradient(135deg, var(--text-primary) 0%, var(--brand-primary) 50%, #00FF88 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              lineHeight: '1.1',
              letterSpacing: '-0.02em'
            }}
          >
            Skills & Expertise
          </h2>
          <div className="flex items-center justify-center gap-2">
            <div
              className="h-[2px] w-10"
              style={{
                background: 'linear-gradient(90deg, transparent, var(--brand-primary))'
              }}
            />
            <div
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ background: 'var(--brand-primary)' }}
            />
            <div
              className="h-[2px] w-10"
              style={{
                background: 'linear-gradient(90deg, var(--brand-primary), transparent)'
              }}
            />
          </div>
        </div>

        {/* Featured Skills Bar - Smaller Cards */}
        <div 
          className={`grid grid-cols-2 md:grid-cols-4 gap-3 mb-10 ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.2s' }}
        >
          {featuredSkills.map((skill, index) => {
            const Icon = skill.icon;
            return (
              <div
                key={index}
                className="glass-card p-3 rounded-lg text-center hover-lift-subtle"
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  animationDelay: `${0.3 + index * 0.05}s`
                }}
              >
                <Icon size={20} className="mx-auto mb-1.5" style={{ color: 'var(--brand-primary)' }} />
                <p className="text-xs mb-0.5" style={{ color: 'var(--text-muted)' }}>{skill.label}</p>
                <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{skill.value}</p>
              </div>
            );
          })}
        </div>

        {/* Skills Grid - Tighter Layout */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {skillCategories.map((category, index) => {
            const Icon = category.icon;
            const isHovered = hoveredCategory === index;
            
            return (
              <div
                key={index}
                className={`relative ${isVisible ? 'animate-scale-in' : 'opacity-0'}`}
                style={{
                  animationDelay: `${0.4 + index * 0.1}s`
                }}
                onMouseEnter={() => setHoveredCategory(index)}
                onMouseLeave={() => setHoveredCategory(null)}
              >
                {/* Card Background Glow */}
                <div
                  className="absolute inset-0 rounded-xl opacity-0 transition-opacity duration-500"
                  style={{
                    background: category.bgGradient,
                    filter: 'blur(20px)',
                    opacity: isHovered ? 0.3 : 0
                  }}
                />
                
                {/* Card Content - Reduced Padding */}
                <div
                  className="relative glass-card rounded-xl p-5 h-full transition-all duration-500"
                  style={{
                    background: isHovered 
                      ? 'rgba(255, 255, 255, 0.05)' 
                      : 'rgba(255, 255, 255, 0.02)',
                    border: `2px solid ${isHovered ? category.color : 'rgba(255, 255, 255, 0.1)'}`,
                    backdropFilter: 'blur(10px)',
                    transform: isHovered ? 'translateY(-5px)' : 'translateY(0)',
                    boxShadow: isHovered 
                      ? `0 20px 40px -12px ${category.color}40` 
                      : '0 10px 30px -10px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className="relative p-2.5 rounded-lg"
                      style={{
                        background: `${category.color}20`,
                        border: `1px solid ${category.color}40`
                      }}
                    >
                      <Icon 
                        size={24} // Smaller Icon
                        style={{ 
                          color: category.color,
                          filter: isHovered ? 'drop-shadow(0 0 10px currentColor)' : 'none',
                          transition: 'all 0.3s ease'
                        }} 
                      />
                      {/* Pulse Animation on Hover */}
                      {isHovered && (
                        <div
                          className="absolute inset-0 rounded-lg animate-ping"
                          style={{
                            background: `${category.color}20`,
                            border: `1px solid ${category.color}40`
                          }}
                        />
                      )}
                    </div>
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full"
                      style={{
                        background: `${category.color}20`,
                        color: category.color,
                        border: `1px solid ${category.color}40`,
                        fontWeight: '600'
                      }}
                    >
                      {category.skills.length} Skills
                    </span>
                  </div>

                  {/* Title - Smaller Font */}
                  <h3
                    className="text-lg font-bold mb-3"
                    style={{ 
                      color: 'var(--text-primary)',
                      background: isHovered 
                        ? `linear-gradient(135deg, ${category.color}, var(--text-primary))`
                        : 'none',
                      WebkitBackgroundClip: isHovered ? 'text' : 'unset',
                      WebkitTextFillColor: isHovered ? 'transparent' : 'var(--text-primary)'
                    }}
                  >
                    {category.title}
                  </h3>

                  {/* Skills List - Tighter Spacing */}
                  <div className="space-y-1.5">
                    {category.skills.map((skill, skillIndex) => (
                      <div
                        key={skillIndex}
                        className="group relative overflow-hidden rounded-md transition-all duration-300 cursor-pointer"
                        style={{
                          background: activeSkill === `${index}-${skillIndex}` 
                            ? `${category.color}15` 
                            : 'rgba(255, 255, 255, 0.03)',
                          border: `1px solid ${
                            activeSkill === `${index}-${skillIndex}` 
                              ? category.color 
                              : 'rgba(255, 255, 255, 0.08)'
                          }`
                        }}
                        onClick={() => setActiveSkill(`${index}-${skillIndex}`)}
                        onMouseLeave={() => setActiveSkill(null)}
                      >
                        {/* Skill Progress Bar Background */}
                        <div
                          className="absolute inset-0 transition-all duration-700 ease-out"
                          style={{
                            background: `linear-gradient(90deg, ${category.color}20 0%, transparent 100%)`,
                            transform: `scaleX(${isHovered ? skill.level / 100 : 0})`,
                            transformOrigin: 'left',
                            opacity: 0.5
                          }}
                        />
                        
                        {/* Skill Content - Compact Padding */}
                        <div className="relative px-2.5 py-1.5 flex items-center justify-between">
                          <span
                            className="text-xs font-medium transition-colors duration-300"
                            style={{ 
                              color: activeSkill === `${index}-${skillIndex}` 
                                ? 'var(--text-primary)' 
                                : 'var(--text-secondary)'
                            }}
                          >
                            {skill.name}
                          </span>
                          <div className="flex items-center gap-2">
                            {/* Projects Count */}
                            {activeSkill === `${index}-${skillIndex}` && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded-full animate-fade-in"
                                style={{
                                  background: `${category.color}20`,
                                  color: category.color,
                                  border: `1px solid ${category.color}40`
                                }}
                              >
                                {skill.projects}
                              </span>
                            )}
                            {/* Level */}
                            <span
                              className="text-[10px] font-bold"
                              style={{ 
                                color: activeSkill === `${index}-${skillIndex}` 
                                  ? category.color 
                                  : 'var(--text-muted)'
                              }}
                            >
                              {skill.level}%
                            </span>
                          </div>
                        </div>
                        
                        {/* Hover Line Animation */}
                        <div
                          className="absolute bottom-0 left-0 h-[1.5px] transition-all duration-300"
                          style={{
                            background: category.color,
                            width: activeSkill === `${index}-${skillIndex}` ? '100%' : '0%'
                          }}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Category Stats */}
                  {isHovered && (
                    <div
                      className="mt-3 pt-3 border-t border-white/10 animate-fade-in"
                      style={{ animationDelay: '0.1s' }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                          Avg. Proficiency
                        </span>
                        <span
                          className="text-xs font-bold"
                          style={{ color: category.color }}
                        >
                          {Math.round(
                            category.skills.reduce((acc, skill) => acc + skill.level, 0) / 
                            category.skills.length
                          )}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA - Smaller Text */}
        <div
          className={`text-center mt-10 ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.8s' }}
        >
          <p
            className="text-sm md:text-base mb-4"
            style={{ color: 'var(--text-secondary)' }}
          >
            Always learning, always shipping. Currently focusing on{' '}
            <span style={{ color: 'var(--brand-primary)', fontWeight: '600' }}>
              production-grade AI systems
            </span>{' '}
            and{' '}
            <span style={{ color: '#00FF88', fontWeight: '600' }}>
              scalable backend automation
            </span>
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          25% { transform: translateY(-20px) rotate(1deg); }
          50% { transform: translateY(10px) rotate(-1deg); }
          75% { transform: translateY(-10px) rotate(2deg); }
        }

        @keyframes animate-fade-down {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes animate-fade-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes animate-scale-in {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }

        @keyframes animate-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .animate-fade-down { animation: animate-fade-down 0.8s ease-out forwards; }
        .animate-fade-up { animation: animate-fade-up 0.8s ease-out forwards; }
        .animate-scale-in { animation: animate-scale-in 0.6s ease-out forwards; }
        .animate-fade-in { animation: animate-fade-in 0.3s ease-out forwards; }

        .hover-lift-subtle {
          transition: all 0.3s ease;
        }

        .hover-lift-subtle:hover {
          transform: translateY(-3px);
        }

        .glass-card {
          position: relative;
          overflow: hidden;
        }

        .glass-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.1),
            transparent
          );
          transition: left 0.5s ease;
        }

        .glass-card:hover::before {
          left: 100%;
        }
      `}</style>
    </section>
  );
};

export default Skills;